import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Zap } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { peekReturnTo } from "@/pages/OAuthConsent";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const back = peekReturnTo();
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav(back || "/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <Toaster richColors position="top-right" />
    <div className="min-h-screen grid md:grid-cols-2 bg-zinc-50">
      <div className="hidden md:flex sidebar-dark relative flex-col justify-between p-12 text-white overflow-hidden">
        <div className="flex items-center gap-2 relative z-10">
          <div className="w-9 h-9 rounded-md bg-[#FF5A00] flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" strokeWidth={2.5}/>
          </div>
          <span className="font-heading font-bold text-xl">LeadForge</span>
        </div>
        <div className="relative z-10 max-w-md">
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-4">The ops OS</div>
          <h1 className="font-heading text-5xl font-black tracking-tight leading-tight">
            Every lead. Every crew. One dashboard.
          </h1>
          <p className="mt-6 text-zinc-400">Sign in and get back to closing jobs.</p>
        </div>
        <div className="relative z-10 text-xs text-zinc-500 font-mono-accent">© 2026 LEADFORGE</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <h2 className="font-heading text-3xl font-bold">Log in</h2>
          <p className="text-zinc-500 mt-2">Access your business workspace.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label>Email</Label>
              <Input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="login-email" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Password</Label>
                <Link to="/forgot-password" className="text-xs text-[#FF5A00] hover:underline" data-testid="login-forgot-link">Forgot?</Link>
              </div>
              <Input type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} data-testid="login-password" />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-11 bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="login-submit-btn">
              {busy ? "Signing in…" : "Log in"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-zinc-500">
            No workspace yet? <Link to="/request-access" className="text-[#FF5A00] font-medium hover:underline" data-testid="login-to-request-access-link">Request access</Link>
          </p>
        </div>
      </div>
    </div>
    </>
  );
}
